import { useState } from "react";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { db } from "../../firebase";
import { initialLetter } from "../../utils/idUtils";
import { t } from "../../i18n/translations";

/**
 * ProfileMenu — tab de perfil completo
 *
 * Props:
 *  user, userProfile, setUserProfile,
 *  lang, darkMode,
 *  members, userFamilies,
 *  coopPanelData,
 *  showToast,
 *  // navegación
 *  setScreen, setActiveTab,
 *  setViewingMember, setViewingWishlist,
 *  setCameFromProfilePicker,
 *  setShowBudgetScreen,
 *  setShowTutorial, setTutorialStep,
 *  // modales que viven en App
 *  setShowEditProfile, setEditProfileForm,
 *  // handlers
 *  toggleDarkMode, toggleNotifications,
 *  handleLogout, loadCoopPanel,
 */
export default function ProfileMenu({
  user,
  userProfile,
  setUserProfile,
  lang,
  darkMode,
  members,
  userFamilies,
  coopPanelData,
  showToast,
  setScreen,
  setActiveTab,
  setViewingMember,
  setViewingWishlist,
  setCameFromProfilePicker,
  setShowBudgetScreen,
  setShowTutorial,
  setTutorialStep,
  setShowEditProfile,
  setEditProfileForm,
  toggleDarkMode,
  toggleNotifications,
  handleLogout,
  loadCoopPanel,
}) {
  const tr = (key) => t(lang, key);

  // ── estado local del picker de wishlus ──
  const [showWlPicker, setShowWlPicker] = useState(false);
  const [showWlEditMode, setShowWlEditMode] = useState(false);
  const [allWishlists, setAllWishlists] = useState(null);

  // ── helpers ──
  const loadAllWishlists = async () => {
    const ids = userProfile?.wishlists || [];
    if (ids.length === 0) { setAllWishlists([]); return; }
    const snaps = await Promise.all(ids.map((id) => getDoc(doc(db, "wishlists", id))));
    setAllWishlists(snaps.filter((s) => s.exists()).map((s) => ({ id: s.id, ...s.data() })));
  };

  const openPicker = async () => {
    if (showWlPicker) { setShowWlPicker(false); setAllWishlists(null); setShowWlEditMode(false); return; }
    await loadAllWishlists();
    setShowWlPicker(true);
  };

  const openWishlist = async (wl) => {
    setShowWlPicker(false);
    setShowWlEditMode(false);
    setAllWishlists(null);
    const wSnap = await getDoc(doc(db, "wishlists", wl.id));
    if (wSnap.exists()) {
      setViewingMember({ id: user.uid, ...userProfile });
      setViewingWishlist({ id: wSnap.id, ...wSnap.data() });
      setCameFromProfilePicker(true);
      setActiveTab("wishlu");
      setScreen("profile");
    }
  };

  const handleCreateNewWishlist = () => {
    // Delegamos al modal simple que vive en App
    setShowWlPicker(false);
    setShowWlEditMode(false);
    setAllWishlists(null);
    // App escucha este evento a través del estado showSimpleWishlistModal
    // Lo disparamos con un custom event para no acoplar más props
    window.dispatchEvent(new CustomEvent("wishlu:create-wishlist"));
  };

  const handleRenameWishlist = async (wl, newName) => {
    if (!newName || newName === wl.name) return;
    await updateDoc(doc(db, "wishlists", wl.id), { name: newName });
    setAllWishlists((wls) => wls.map((w) => (w.id === wl.id ? { ...w, name: newName } : w)));
    showToast(lang === "es" ? "✅ Nombre actualizado" : "✅ Name updated");
  };

  const handleDeleteWishlist = async (wl) => {
    if (!window.confirm(lang === "es" ? "¿Eliminar esta Wishlu? Los regalos se perderán." : "Delete this Wishlu? Its gifts will be lost.")) return;
    try {
      const { deleteDoc } = await import("firebase/firestore");
      await deleteDoc(doc(db, "wishlists", wl.id));
    } catch (e) {}
    const newWls = (userProfile?.wishlists || []).filter((id) => id !== wl.id);
    const newFW = {};
    Object.entries(userProfile?.familyWishlists || {}).forEach(([fid, wid]) => { if (wid !== wl.id) newFW[fid] = wid; });
    await updateDoc(doc(db, "users", user.uid), { wishlists: newWls, familyWishlists: newFW });
    setUserProfile((p) => ({ ...p, wishlists: newWls, familyWishlists: newFW }));
    setAllWishlists((wls) => wls.filter((w) => w.id !== wl.id));
    showToast(lang === "es" ? "🗑️ Wishlu eliminada" : "🗑️ Wishlu deleted");
  };

  // ── coop count ──
  const confirmedKeys = userProfile?.confirmedCoopKeys || [];
  const myMember = members.find((m) => m.id === user?.uid);
  const coopCount =
    coopPanelData !== null
      ? coopPanelData.length
      : (myMember?.gifts || []).reduce((acc, g) => {
          return (
            acc +
            (g.cooperations || []).filter((c) => {
              const key = (myMember.wishlistId || "") + "_" + (g.id || "") + "_" + (c.uid || c.name || "") + "_" + (c.at || "");
              return !confirmedKeys.includes(key);
            }).length
          );
        }, 0);

  // ── colores ──
  const border = darkMode ? "#2e2e2e" : "#e8e2d9";
  const bg = darkMode ? "#1e1e1e" : "#fff";
  const textColor = darkMode ? "#e8e2d9" : "#1a1a1a";
  const rowStyle = { background: bg, border: `1px solid ${border}`, borderRadius: 16, marginBottom: 8, overflow: "hidden" };
  const rowInner = { display: "flex", alignItems: "center", gap: 14, padding: "14px 18px", cursor: "pointer", fontSize: 15 };
  const iconStyle = { fontSize: 20, width: 28, textAlign: "center" };
  const toggle = (active) => ({
    background: active ? "#d4a853" : "#e0dbd2",
    borderRadius: 20, width: 44, height: 24,
    position: "relative", cursor: "pointer",
    transition: "background 0.2s", flexShrink: 0,
  });
  const toggleThumb = (active) => ({
    position: "absolute", top: 3,
    left: active ? 23 : 3,
    width: 18, height: 18,
    borderRadius: "50%", background: "#fff",
    transition: "left 0.2s",
    boxShadow: "0 1px 4px rgba(0,0,0,0.2)",
  });

  return (
    <div>
      {/* ── Avatar + editar perfil ── */}
      <div style={{ padding: "0 0 16px", borderBottom: `1px solid ${darkMode ? "#2e2e2e" : "#f0ece6"}`, marginBottom: 16, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{ width: 48, height: 48, borderRadius: "50%", background: userProfile?.color || "#9b59b6", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, color: "#fff", fontFamily: "Playfair Display, serif", fontWeight: 700, flexShrink: 0 }}>
            {initialLetter(userProfile?.name)}
          </div>
          <div>
            <div style={{ fontFamily: "Playfair Display, serif", fontSize: 18, color: textColor }}>{userProfile?.name}</div>
            <div style={{ fontSize: 12, color: "#999", marginTop: 2 }}>{userProfile?.email}</div>
          </div>
        </div>
        <button
          style={{ background: "none", border: "1px solid #e0dbd2", borderRadius: 8, padding: "6px 12px", fontSize: 12, cursor: "pointer", color: darkMode ? "#e8e2d9" : "#555" }}
          onClick={() => {
            setEditProfileForm({
              name: userProfile?.name || "",
              birthday: userProfile?.birthday || "",
              color: userProfile?.color || "#9b59b6",
              language: userProfile?.language || "en",
              currency: userProfile?.currency || "USD",
              mercadopago: userProfile?.mercadopago || "",
              paypal: userProfile?.paypal || "",
              country: userProfile?.country || "",
              amazonWishlist: userProfile?.amazonWishlist || "",
            });
            setShowEditProfile(true);
          }}
        >
          {tr("editProfile")}
        </button>
      </div>

      {/* ── Mis Wishlus picker ── */}
      <div style={{ ...rowStyle }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "14px 18px", fontSize: 15 }}>
          <span style={{ fontSize: 20, width: 28, textAlign: "center", flexShrink: 0 }}>🎁</span>
          <span style={{ flex: 1, cursor: "pointer" }} onClick={openPicker}>
            {lang === "es" ? "Mis Wishlus" : lang === "fi" ? "Wishlu-listani" : "My Wishlus"}
          </span>
          {showWlPicker && (
            <button
              style={{ background: showWlEditMode ? (darkMode ? "#2e2e2e" : "#f5f0ea") : "none", border: "none", fontSize: 16, cursor: "pointer", padding: "4px 6px", borderRadius: 8, color: showWlEditMode ? "#d4a853" : "#888", lineHeight: 1 }}
              onClick={(e) => { e.stopPropagation(); setShowWlEditMode((v) => !v); }}
            >⚙️</button>
          )}
          {showWlPicker && (
            <button
              style={{ background: "none", border: "none", fontSize: 22, cursor: "pointer", padding: "2px 4px", borderRadius: 8, color: "#d4a853", fontWeight: 700, lineHeight: 1 }}
              onClick={(e) => { e.stopPropagation(); handleCreateNewWishlist(); }}
            >＋</button>
          )}
          <span style={{ color: "#aaa", fontSize: 13, cursor: "pointer", padding: "2px 6px", flexShrink: 0 }} onClick={openPicker}>
            {showWlPicker ? "▲" : "▼"}
          </span>
        </div>

        {showWlPicker && (
          <div style={{ borderTop: `1px solid ${darkMode ? "#2e2e2e" : "#f0ece6"}` }}>
            {allWishlists === null && (
              <div style={{ padding: "12px 18px", fontSize: 13, color: "#aaa" }}>{tr("loading")}</div>
            )}
            {allWishlists !== null && allWishlists.length === 0 && (
              <div style={{ padding: "12px 18px", fontSize: 13, color: "#aaa", textAlign: "center" }}>
                {lang === "es" ? "No tienes Wishlus aún" : "No Wishlus yet"}
              </div>
            )}
            {(allWishlists || []).map((wl) => (
              <div key={wl.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: showWlEditMode ? "8px 12px 8px 18px" : "12px 18px", background: "transparent", borderBottom: `1px solid ${darkMode ? "#2e2e2e" : "#f0ece6"}` }}>
                {showWlEditMode ? (
                  <input
                    className="campo-input"
                    style={{ margin: 0, flex: 1, fontSize: 13, padding: "6px 10px" }}
                    defaultValue={wl.name}
                    onBlur={(e) => handleRenameWishlist(wl, e.target.value.trim())}
                    onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
                  />
                ) : (
                  <div style={{ flex: 1, minWidth: 0, cursor: "pointer" }} onClick={() => openWishlist(wl)}>
                    <div style={{ fontSize: 14, color: textColor, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{wl.name}</div>
                    <div style={{ fontSize: 12, color: "#999", marginTop: 2 }}>
                      {(wl.gifts || []).length} {tr("gifts")}
                      {(() => {
                        const assignedFamilyIds = Object.entries(userProfile?.familyWishlists || {})
                          .filter(([, wid]) => wid === wl.id)
                          .map(([fid]) => fid);
                        const assignedFamilies = userFamilies.filter((f) => assignedFamilyIds.includes(f.id));
                        return assignedFamilies.length > 0
                          ? <span style={{ marginLeft: 6, color: "#d4a853" }}>· {assignedFamilies.map((f) => f.name).join(", ")}</span>
                          : <span style={{ marginLeft: 6, color: "#bbb" }}>· {lang === "es" ? "sin grupo" : lang === "fi" ? "ei ryhmää" : "no group"}</span>;
                      })()}
                    </div>
                  </div>
                )}
                {showWlEditMode && (
                  <button
                    style={{ background: "none", border: "none", fontSize: 16, cursor: "pointer", color: "#c0392b", padding: "4px 6px", borderRadius: 8, flexShrink: 0 }}
                    onClick={(e) => { e.stopPropagation(); handleDeleteWishlist(wl); }}
                  >🗑️</button>
                )}
              </div>
            ))}
            {showWlEditMode && (allWishlists || []).length > 0 && (
              <div style={{ padding: "8px 18px", fontSize: 11, color: "#aaa", textAlign: "center" }}>
                {lang === "es" ? "Edita el nombre y toca fuera para guardar · 🗑️ para eliminar" : "Edit name and tap outside to save · 🗑️ to delete"}
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Notificaciones ── */}
      <div style={rowStyle}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 18px", cursor: "pointer" }} onClick={toggleNotifications}>
          <div style={{ display: "flex", alignItems: "center", gap: 14, fontSize: 15 }}>
            <span style={iconStyle}>🔔</span>
            <span>{tr("birthdayNotifications")}</span>
          </div>
          <div style={toggle(userProfile?.birthdayNotifications !== false)}>
            <div style={toggleThumb(userProfile?.birthdayNotifications !== false)} />
          </div>
        </div>
      </div>

      {/* ── Dark mode ── */}
      <div style={rowStyle}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 18px", cursor: "pointer" }} onClick={toggleDarkMode}>
          <div style={{ display: "flex", alignItems: "center", gap: 14, fontSize: 15 }}>
            <span style={iconStyle}>{darkMode ? "☀️" : "🌙"}</span>
            <span>{tr("darkMode")}</span>
          </div>
          <div style={toggle(darkMode)}>
            <div style={toggleThumb(darkMode)} />
          </div>
        </div>
      </div>

      {/* ── Presupuesto ── */}
      <div style={rowStyle}>
        <div style={rowInner} onClick={() => setShowBudgetScreen(true)}>
          <span style={iconStyle}>📊</span>
          <span style={{ flex: 1 }}>{tr("budgetGeneral")}</span>
          <span style={{ color: "#aaa", fontSize: 13 }}>›</span>
        </div>
      </div>

      {/* ── Cooperaciones ── */}
      <div style={rowStyle}>
        <div style={rowInner} onClick={loadCoopPanel}>
          <span style={iconStyle}>💸</span>
          <span style={{ flex: 1 }}>{lang === "es" ? "Cooperaciones" : lang === "fi" ? "Osallistumiset" : "Contributions"}</span>
          {coopCount > 0 && (
            <span style={{ background: "#ff0505", color: "#fff", borderRadius: 12, fontSize: 11, fontWeight: 700, padding: "2px 8px", minWidth: 20, textAlign: "center" }}>
              {coopCount}
            </span>
          )}
          <span style={{ color: "#aaa", fontSize: 13 }}>›</span>
        </div>
      </div>

      {/* ── Contacto ── */}
      <div style={rowStyle}>
        <a href="mailto:wishlu.support@gmail.com" style={{ display: "flex", alignItems: "center", gap: 14, padding: "14px 18px", fontSize: 15, color: textColor, textDecoration: "none" }}>
          <span style={iconStyle}>💬</span>
          <span>{lang === "es" ? "Contacto y soporte" : lang === "fi" ? "Yhteystiedot ja tuki" : "Contact & support"}</span>
          <span style={{ color: "#aaa", fontSize: 13, marginLeft: "auto" }}>›</span>
        </a>
      </div>

      {/* ── Tutorial ── */}
      <div style={rowStyle}>
        <div
          style={rowInner}
          onClick={() => {
            try { localStorage.removeItem("whislu_tutorial_done"); } catch (e) {}
            setTutorialStep(0);
            setActiveTab("wishlu");
            setScreen("family");
            setShowTutorial(true);
          }}
        >
          <span style={iconStyle}>🎓</span>
          <span>{lang === "es" ? "Ver tutorial" : lang === "fi" ? "Näytä opastus" : "View tutorial"}</span>
        </div>
      </div>

      {/* ── Cerrar sesión ── */}
      <div style={rowStyle}>
        <div style={{ ...rowInner, color: "#c0392b" }} onClick={handleLogout}>
          <span style={iconStyle}>🚪</span>
          <span>{tr("signOutMenu")}</span>
        </div>
      </div>
    </div>
  );
}
